
/* This code allows auth-pages to make a decent guess at a user's country before
   they are logged in. It relies on the following assumptions being true:
   - Navigation to auth pages at least once comes from the country-specific websites, e.g.
     climate.de, climate.ca, etc.
   - The hostnames in those URLs map one-to-one with countries.

   The above may change later, and if so, a different solution will be needed. This code
   is necessarily coupled to the assumptions above.

   ANTI-PATTERN: Do not use this guessing approach if you are on a post-login page. You would
   have access to the user's country from their account data in that case.
*/

import { isClimateFieldViewAppUrl, getReferrerUrl, getHostnameFromUrl } from './url';
import { queryParam } from './queryString';

const getCountryFromReferrerUrl = (url, supportInfo) => {
  if (!url) { return null; }

  /* If the referrer URL is from one of the Climate FieldView apps, it should not be used to
     determine country. Why? At time of writing, the top-level climate.com domain serves US
     customers but also the globally hosts the app for customers from all countries. */
  if (isClimateFieldViewAppUrl(url)) { return null; }

  // Match URL to one of the support domains that implies a country.
  const hostname = getHostnameFromUrl(url);
  return Object.keys(supportInfo).find(countryCode =>
    getHostnameFromUrl(supportInfo[countryCode].website_url) === hostname);
};

/** Return a country code if it can be determined by the referrer of the current page, or
    from result of a previously successful determination stored locally.
    Side effect: a successful determination will be stored locally.
    @param {object} supportInfo Store-format support info used for mapping hostnames to countries.
    @return {string} Country code if it could be determined/looked up, null if not. */
export const getReferrerCountryCode = (supportInfo) => {
  const referrerUrl = getReferrerUrl();
  let countryCode = getCountryFromReferrerUrl(referrerUrl, supportInfo);
  if (countryCode) {
    // Save this country as the last successful determination.
    localStorage.setItem('referrerCountryCode', countryCode);
  } else {
    // If could not determine country by referrer URL, use the last successful determination.
    countryCode = localStorage.getItem('referrerCountryCode') || null;
  }
  return countryCode;
};


export const extractEmailPhoneNo = (supportInfo, countryCode) => {
  const entry = supportInfo[countryCode];
  return { supportEmail: entry.support_email, supportPhoneNumber: entry.support_phone_number };
};

export const getLandingPageUrls = (supportInfo) => {
  const landingPageUrls = {};
  Object.keys(supportInfo).forEach(countryCode => {
    landingPageUrls[countryCode] = supportInfo[countryCode].website_url;
  });
  return landingPageUrls;
};

export const getCountryCodeFromQueryParam = () => {
  return queryParam('countryCode') || queryParam('countrycode');
};
